import { z } from 'zod';
import { GitCiStatus, GitLinkedTarget, GitTimeMeta, cycleBreakdown, GitPersonRef, } from './git.js';
import { WorkflowCategory } from './project.js';
import { calendarDateIn, zonedTimeToUtc } from './timezone.js';
/**
 * Tab Git — chỉ số luồng, danh sách "Cần chú ý", tài khoản git ↔ người
 * (APM-GIT-j). docs/plan-git-integration.md §7.1, §7.2, §4.1 (`git_identity`).
 *
 * Tệp riêng (không nối vào `git.ts`) để các task `APM-GIT-*` chạy song song không
 * giẫm lên nhau. Hai phần:
 *
 * - **Hàm thuần**: phân vị, tuần theo múi giờ tổ chức, nhóm tuổi MR, "nhánh chính
 *   đỏ từ lúc nào". API (endpoint, thông báo) và web dùng chung một định nghĩa.
 * - **Hình API** của ba endpoint ở `git-insights.controller.ts`.
 *
 * ⚠️ **Không** bảng xếp hạng theo người (§7.1): mọi số ở đây là của **cả dự án**
 * (hoặc một kho). Tài khoản git ↔ người chỉ để danh sách "Cần chú ý" nói được
 * "MR của ai đang chờ" — không có endpoint nào đếm theo người.
 */
// ── Ngưỡng (§7.1, §7.2) ─────────────────────────────────────────────────────
/** Số tuần của các biểu đồ theo tuần; tuần cuối là tuần chứa `asOf`. */
export const GIT_INSIGHTS_WEEKS = 12;
/** Ngưỡng mặc định của danh sách "Cần chú ý" — §7.2. Giao diện nêu đúng các số này. */
export const GIT_ATTENTION_THRESHOLDS = {
    /** MR mở, không nháp, chưa ai review quá chừng này giờ. */
    reviewWaitHours: 24,
    /** MR mở không cập nhật quá chừng này ngày. */
    staleChangeRequestDays: 7,
    /** Nhánh của việc đang làm không có commit mới quá chừng này ngày. */
    staleBranchDays: 5,
    /** Nhánh chính đỏ quá chừng này giờ ⇒ thông báo `git_default_pipeline_failed`. */
    defaultBranchRedHours: 2,
    /** "Code đã vào, việc chưa đóng": chỉ xét MR merge trong chừng này ngày gần `asOf`. */
    mergedWindowDays: 30,
    /** "Mã không tồn tại": lời commit trên nhánh mặc định trong chừng này ngày. */
    unknownCodeCommitDays: 14,
};
/** "Việc không có task" (§7.1): commit trên nhánh mặc định trong chừng này ngày. */
export const GIT_UNLINKED_WORK_DAYS = 30;
/** Thông báo `git_token_expiring` — trước hạn token chừng này ngày, một lần. */
export const GIT_TOKEN_EXPIRY_WARN_DAYS = 14;
/** Mỗi loại của "Cần chú ý" trả tối đa chừng này mục (cũ nhất trước); `counts` mang tổng thật. */
export const GIT_ATTENTION_ITEMS_PER_KIND = 20;
const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
// ── Phân vị ─────────────────────────────────────────────────────────────────
/**
 * Phân vị `p` (0…1) — nội suy tuyến tính giữa hai hạng kề nhau (cách R gọi là
 * "type 7", cùng cách của Excel `PERCENTILE.INC`). Rỗng ⇒ `null`, **không** 0:
 * "tuần này không có MR nào" khác "MR merge ngay lập tức".
 *
 * Trung vị và p85, không trung bình (§7.1): một MR treo ba tuần kéo trung bình
 * của cả đội lên gấp đôi, còn trung vị nói "MR thường mất bao lâu" và p85 nói
 * "MR chậm thì chậm cỡ nào".
 */
export function percentile(values, p) {
    if (values.length === 0)
        return null;
    if (!(p >= 0 && p <= 1))
        throw new RangeError('percentile: p must be within 0…1');
    const sorted = [...values].sort((a, b) => a - b);
    const rank = p * (sorted.length - 1);
    const lo = Math.floor(rank);
    const hi = Math.ceil(rank);
    const value = sorted[lo] + (sorted[hi] - sorted[lo]) * (rank - lo);
    return Math.round(value);
}
export const GitDistribution = z.object({
    /** Số mẫu — 0 thì hai số dưới là `null`. */
    count: z.number().int().nonnegative(),
    medianMs: z.number().int().nonnegative().nullable(),
    p85Ms: z.number().int().nonnegative().nullable(),
});
export function distributionOf(values) {
    return {
        count: values.length,
        medianMs: percentile(values, 0.5),
        p85Ms: percentile(values, 0.85),
    };
}
// ── Bốn pha của chu trình MR (§7.1) ─────────────────────────────────────────
export const GitCyclePhase = z.enum(['coding', 'pickup', 'review', 'deploy']);
/**
 * Gom `cycleBreakdown` của nhiều MR thành mẫu của từng pha. Pha thiếu một đầu
 * mốc (MR chưa ai review, chưa lên production) **không** góp mẫu vào pha ấy —
 * nó vẫn góp vào các pha còn lại. Ghi 0 thay vì bỏ là kéo trung vị xuống bằng
 * đúng những MR bỏ qua bước đó.
 */
export function cycleSamples(samples) {
    const out = { coding: [], pickup: [], review: [], deploy: [], leadTime: [] };
    for (const sample of samples) {
        const phases = cycleBreakdown(sample, sample.deployedAt);
        if (phases.codingMs !== null)
            out.coding.push(phases.codingMs);
        if (phases.pickupMs !== null)
            out.pickup.push(phases.pickupMs);
        if (phases.reviewMs !== null)
            out.review.push(phases.reviewMs);
        if (phases.deployMs !== null)
            out.deploy.push(phases.deployMs);
        if (phases.leadTimeMs !== null)
            out.leadTime.push(phases.leadTimeMs);
    }
    return out;
}
/** Cộng ngày trên một **ngày lịch** (không phải trên một mốc thời gian). */
function addCalendarDays(date, days) {
    const [year, month, day] = date.split('-').map(Number);
    const shifted = new Date(Date.UTC(year, month - 1, day + days));
    const pad = (n, width = 2) => String(n).padStart(width, '0');
    return `${pad(shifted.getUTCFullYear(), 4)}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
}
/**
 * `count` tuần (thứ Hai → thứ Hai) **theo múi giờ của tổ chức**, tuần cuối là
 * tuần chứa `asOf` — mép phải của biểu đồ là `asOf` (§7.3).
 *
 * Không `toISOString().slice(0, 10)`: ở +07:00 nó xếp một MR merge lúc 06:00 sáng
 * thứ Hai vào tuần **trước** (cạm bẫy múi giờ ở CLAUDE.md).
 */
export function gitWeeks(asOf, timezone, count = GIT_INSIGHTS_WEEKS) {
    const today = calendarDateIn(asOf, timezone);
    const [year, month, day] = today.split('-').map(Number);
    // Thứ trong tuần của một ngày lịch — phép tính trên lịch, không trên mốc thời gian.
    const sinceMonday = (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
    const monday = addCalendarDays(today, -sinceMonday);
    return Array.from({ length: count }, (_, i) => {
        const start = addCalendarDays(monday, -7 * (count - 1 - i));
        return {
            start,
            from: zonedTimeToUtc(start, 0, timezone),
            to: zonedTimeToUtc(addCalendarDays(start, 7), 0, timezone),
        };
    });
}
/** Chỉ số của tuần chứa mốc `at`; ngoài khoảng ⇒ `-1`. */
export function weekIndexOf(weeks, at) {
    const ms = at instanceof Date ? at.getTime() : Date.parse(at);
    return weeks.findIndex((week) => ms >= week.from.getTime() && ms < week.to.getTime());
}
// ── MR đang mở theo tuổi (§7.1) ─────────────────────────────────────────────
export const GitOpenAgeBucket = z.enum(['under1d', 'd1to3', 'd3to7', 'over7d']);
/** `< 1` · `1–3` · `3–7` · `> 7` ngày, tính từ lúc tạo MR tới `asOf`. Đúng 7 ngày vẫn là `3–7`. */
export function openAgeBucket(createdAt, asOf) {
    const ms = (asOf instanceof Date ? asOf.getTime() : Date.parse(asOf)) -
        (createdAt instanceof Date ? createdAt.getTime() : Date.parse(createdAt));
    if (ms < DAY_MS)
        return 'under1d';
    if (ms < 3 * DAY_MS)
        return 'd1to3';
    if (ms <= 7 * DAY_MS)
        return 'd3to7';
    return 'over7d';
}
function ms(value) {
    if (value === null)
        return null;
    const out = value instanceof Date ? value.getTime() : Date.parse(value);
    return Number.isNaN(out) ? null : out;
}
/**
 * Nhánh mặc định **đỏ từ lúc nào** tại `asOf` — `null` khi không đỏ.
 *
 * Xét các lượt CI của **một** nhánh, mới nhất trước, và chỉ những lượt đã **kết
 * thúc** lúc `asOf` (`success` / `failed`; xong sau `asOf` thì lúc ấy còn chạy):
 *
 * - lượt kết thúc mới nhất là `failed` ⇒ đỏ, và đỏ **từ** lượt `failed` sớm nhất
 *   của chuỗi `failed` liền nhau đứng sau lượt `success` gần nhất;
 * - `canceled` và lượt chưa xong **bỏ qua**: lượt sửa đang chạy chưa làm nhánh
 *   xanh, và một lượt bị huỷ không nói gì về code.
 *
 * Nhờ vậy "đỏ > 2 giờ" đo từ lúc nhánh **bắt đầu** đỏ, và lượt sửa thứ hai cũng
 * hỏng không đặt lại đồng hồ.
 */
export function defaultBranchRedSince(pipelines, asOf = null) {
    const limit = ms(asOf);
    const concluded = pipelines
        .map((pipeline) => ({
        status: pipeline.status,
        created: ms(pipeline.createdAtExt) ?? 0,
        // Bản sao chưa có `finished_at` của một lượt đã xong ⇒ lấy mốc tạo.
        ended: ms(pipeline.finishedAt) ?? ms(pipeline.createdAtExt) ?? 0,
    }))
        .filter((row) => (row.status === 'success' || row.status === 'failed') &&
        (limit === null || (row.created <= limit && row.ended <= limit)))
        .sort((a, b) => b.created - a.created);
    let since = null;
    for (const row of concluded) {
        if (row.status === 'success')
            break;
        since = row.ended;
    }
    return since === null ? null : new Date(since);
}
// ── Hình API: chỉ số (§7.1) ─────────────────────────────────────────────────
export const GitInsightsQuery = z.object({ asOf: z.iso.datetime().optional() });
/** Nguồn của "tần suất triển khai": deployment lên production, hay bản phát hành (tag) khi kho không có deployment. */
export const GitDeploySource = z.enum(['deployments', 'tags', 'mixed', 'none']);
export const GitInsightsWeek = z.object({
    /** Thứ Hai đầu tuần (ngày lịch của tổ chức). */
    start: z.iso.date(),
    /** Số MR merge trong tuần — mẫu của bốn pha tuần ấy. */
    merged: z.number().int().nonnegative(),
    coding: GitDistribution,
    pickup: GitDistribution,
    review: GitDistribution,
    deploy: GitDistribution,
    /** Lead time của MR merge trong tuần (đã lên production lúc `asOf`). */
    leadTime: GitDistribution,
    /** Số lượt triển khai `success` lên production (hoặc bản phát hành — xem `deploySource`). */
    deploys: z.number().int().nonnegative(),
});
/**
 * `GET /projects/:projectId/git/insights` — bốn chỉ số luồng §7.1 và tỷ lệ "việc
 * không có task". Tính lúc đọc từ bản sao, không bảng tổng hợp (§7).
 *
 * ⚠️ Thứ **không** dựng lại được ở `asOf` quá khứ (giữ giá trị hiện tại): nháp hay
 * không của MR (pha Viết của MR **đang nháp** lúc đọc), `ready_at` / `first_review_at`
 * là mốc ta thấy lần đầu; MR từng đóng rồi mở lại (xem `stateAt`).
 */
export const GitInsights = GitTimeMeta.extend({
    /** Múi giờ của tổ chức — tuần cắt theo nó. */
    timezone: z.string(),
    weeks: z.array(GitInsightsWeek),
    /** Cả khoảng `weeks`: bốn pha của mọi MR merge trong khoảng. */
    cycle: z.object({
        merged: z.number().int().nonnegative(),
        coding: GitDistribution,
        pickup: GitDistribution,
        review: GitDistribution,
        deploy: GitDistribution,
    }),
    leadTime: GitDistribution,
    /** MR đang mở lúc `asOf`, theo tuổi. Đủ bốn nhóm, kể cả nhóm 0. */
    openByAge: z.array(z.object({ bucket: GitOpenAgeBucket, count: z.number().int().nonnegative() })),
    openTotal: z.number().int().nonnegative(),
    deploySource: GitDeploySource,
    /** % commit trên nhánh mặc định trong `GIT_UNLINKED_WORK_DAYS` ngày tới `asOf` không có liên kết hiệu lực. */
    unlinkedWork: z.object({
        days: z.number().int().positive(),
        commits: z.number().int().nonnegative(),
        unlinked: z.number().int().nonnegative(),
        /** 0…100, một chữ số thập phân; `null` khi không có commit nào. */
        percent: z.number().nullable(),
    }),
});
// ── Hình API: "Cần chú ý" (§7.2) ────────────────────────────────────────────
export const GitAttentionKind = z.enum([
    /** Việc Xong, code chưa vào: việc nhóm `done` còn MR `closes` đang mở. */
    'done_with_open_cr',
    /** Code đã vào, việc chưa đóng: MR `closes` đã merge, việc chưa `done`. */
    'merged_not_done',
    /** MR mở, không nháp, chưa ai review > 24 giờ. */
    'review_waiting',
    /** MR mở không cập nhật > 7 ngày. */
    'stale_change_request',
    /** Việc đang làm có nhánh liên kết, không commit mới > 5 ngày. */
    'stale_branch',
    /** MR mở không có liên kết hiệu lực tới việc nào. */
    'unlinked_change_request',
    /** Văn bản nhắc `KEY-n` mà dự án không có số đó. */
    'unknown_code',
    /** Lượt CI kết thúc mới nhất của nhánh mặc định `failed`. */
    'default_branch_red',
]);
/** Việc trong một mục "Cần chú ý", kèm nhóm trạng thái **hiện tại** của nó. */
export const GitAttentionTarget = GitLinkedTarget.extend({ category: WorkflowCategory });
export const GitAttentionItem = z.object({
    kind: GitAttentionKind,
    repositoryId: z.uuid(),
    repositoryPath: z.string(),
    /** Lệch từ lúc nào (chờ review từ, không cập nhật từ, đỏ từ…); `null` khi không có một mốc như thế. */
    since: z.iso.datetime().nullable(),
    changeRequest: z
        .object({
        number: z.number().int(),
        title: z.string(),
        webUrl: z.string(),
        isDraft: z.boolean(),
        authorUsername: z.string().nullable(),
        authorName: z.string().nullable(),
        /** Người của ChillPM đứng sau tài khoản tác giả — để biết **nói chuyện với ai**, không để chấm. */
        author: GitPersonRef.nullable(),
    })
        .nullable(),
    branch: z.string().nullable(),
    commit: z
        .object({ sha: z.string(), title: z.string(), webUrl: z.string().nullable() })
        .nullable(),
    pipeline: z
        .object({ status: GitCiStatus, sha: z.string(), webUrl: z.string().nullable() })
        .nullable(),
    targets: z.array(GitAttentionTarget),
    /** `unknown_code`: các mã không trỏ tới việc nào của dự án. */
    codes: z.array(z.string()),
    /** `merged_not_done`: vì sao luật `cr_merged` không chuyển việc (`git_rule_fired`); `null` = luật không chạy (đang tắt). */
    skipReason: z.string().nullable(),
});
/**
 * `GET /projects/:projectId/git/attention` — danh sách lệch pha §7.2.
 *
 * ⚠️ Thứ **không** dựng lại được ở `asOf` quá khứ: **trạng thái của việc** (luôn là
 * nhóm hiện tại — cùng quy ước với màn Bản phát hành), nháp hay không của MR, mốc
 * cập nhật cuối của MR và commit cuối của nhánh (máy chủ git chỉ giữ giá trị mới
 * nhất; mốc ấy nằm **sau** `asOf` thì mục bị bỏ qua thay vì đoán).
 */
export const GitAttention = GitTimeMeta.extend({
    items: z.array(GitAttentionItem),
    /** Tổng thật của từng loại — `items` chỉ giữ `GIT_ATTENTION_ITEMS_PER_KIND` mục mỗi loại. */
    counts: z.record(GitAttentionKind, z.number().int().nonnegative()),
});
// ── Tài khoản git ↔ người (§4.1) ────────────────────────────────────────────
export const GIT_EXTERNAL_USERNAME_MAX = 120;
export const GIT_EXTERNAL_EMAIL_MAX = 255;
/** Khoá của một tài khoản git: tên đăng nhập (tác giả / reviewer MR) hay email (tác giả commit — APM-GIT-u). */
export const GitIdentityKind = z.enum(['username', 'email']);
/** Tài khoản được thấy ở đâu trong bản sao của dự án. */
export const GitIdentitySeenAs = z.enum(['change_request_author', 'reviewer', 'commit_author']);
/**
 * Một tài khoản git đã thấy ở dự án (MR, review, commit), và người đứng sau nó.
 *
 * Hàng `username` không mang email (chuỗi tự khai, §0); hàng `email` mang chính
 * email vì đó là khoá — người có `git:manage` vốn đọc được nó trên máy chủ git.
 */
export const GitIdentityView = z.object({
    hostId: z.uuid(),
    hostName: z.string(),
    kind: GitIdentityKind,
    externalUsername: z.string().nullable(),
    externalEmail: z.string().nullable(),
    /** Tên hiển thị máy chủ git trả kèm (tác giả MR / reviewer / tên ở commit mới nhất). */
    externalName: z.string().nullable(),
    seenAs: z.array(GitIdentitySeenAs),
    /** Số commit nhánh mặc định mang email này (hàng username: gộp từ email đã khớp qua MR). */
    commitCount: z.number().int().nonnegative(),
    /** Lần thấy cuối (MR cập nhật / commit) — để xếp; `null` khi không rõ. */
    lastSeenAt: z.iso.datetime().nullable(),
    person: GitPersonRef.nullable(),
    /** Có hàng gắn tay (`git_identity`) — gỡ được. Khớp email thì không có gì để gỡ. */
    manual: z.boolean(),
});
export const GitIdentityPerson = z.object({
    id: z.uuid(),
    displayName: z.string(),
    /** Đang là thành viên của dự án này — xếp trước trong ô chọn. */
    inProject: z.boolean(),
});
/** `GET /projects/:projectId/git/identities` — màn Cài đặt; không theo thời gian ⇒ không `asOf`. */
export const GitIdentityList = z.object({
    items: z.array(GitIdentityView),
    /** Người chọn được: thành viên đang hoạt động của **tổ chức** (gắn ở cấp tổ chức), người trong dự án xếp trước. */
    people: z.array(GitIdentityPerson),
});
/**
 * `PUT /projects/:projectId/git/identities` — `userId: null` = gỡ gắn tay. Đúng
 * **một** trong `externalUsername` / `externalEmail`.
 */
export const MapGitIdentityInput = z
    .object({
    hostId: z.uuid(),
    externalUsername: z.string().trim().min(1).max(GIT_EXTERNAL_USERNAME_MAX).optional(),
    externalEmail: z
        .string()
        .trim()
        .toLowerCase()
        .min(1)
        .max(GIT_EXTERNAL_EMAIL_MAX)
        .optional(),
    userId: z.uuid().nullable(),
})
    .superRefine((value, ctx) => {
    const keys = [value.externalUsername, value.externalEmail].filter((v) => v !== undefined);
    if (keys.length !== 1) {
        ctx.addIssue({
            code: 'custom',
            path: ['externalUsername'],
            message: 'Cần đúng một trong externalUsername / externalEmail',
        });
    }
});
/** Giờ của một ngưỡng — để giao diện và test nói cùng một con số. */
export const GIT_REVIEW_WAIT_MS = GIT_ATTENTION_THRESHOLDS.reviewWaitHours * HOUR_MS;
export const GIT_STALE_CHANGE_REQUEST_MS = GIT_ATTENTION_THRESHOLDS.staleChangeRequestDays * DAY_MS;
export const GIT_STALE_BRANCH_MS = GIT_ATTENTION_THRESHOLDS.staleBranchDays * DAY_MS;
export const GIT_DEFAULT_BRANCH_RED_MS = GIT_ATTENTION_THRESHOLDS.defaultBranchRedHours * HOUR_MS;
//# sourceMappingURL=git-insights.js.map